/**
 * Horizontal carousel between sibling notes in the detail view.
 *
 * Same gesture standard as the rest of the product (`@/lib/gestures`):
 * recognizer with deadzone + X axis-lock, fling, haptic on commit, and
 * respects `prefers-reduced-motion` (no animation; direct navigation).
 *
 * Analogous to the template's drawer carousel (`todo-detail-route-view`), but Folio's
 * detail remounts via route (`<Show keyed>` on pageId), so instead
 * of a multi-panel track we only animate the EXIT of the current panel and then
 * navigate; the new page enters with its own render. Ratio commit
 * (`DRAWER_CAROUSEL` style): confirms at the lesser of fixed travel or width fraction.
 *
 * Only governs horizontal axis: vertical drag yields to editor
 * scrolling. TipTap contenteditable carries `data-no-swipe` to avoid stealing
 * text selection; here we also abort if gesture start lands on an editable.
 */
import { createSignal } from "solid-js";

import { closestSelector, isEditableTarget } from "@/lib/gestures/guards";
import { prefersReducedMotion } from "@/lib/gestures/motion";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { vibrate } from "@/lib/gestures/haptics";
import { pointerHandlers } from "@/lib/gestures/solid";
import { NOTE_CAROUSEL } from "@/lib/gestures/presets";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  commitThresholdRatio: COMMIT_RATIO,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  transitionMs: TRANSITION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  hapticMs: HAPTIC_MS,
  fling: FLING,
} = NOTE_CAROUSEL;

interface NoteCarouselOptions {
  /** ID of previous page in flat list, or null if first. */
  getPrevId: () => string | null;
  /** ID of next page in flat list, or null if last. */
  getNextId: () => string | null;
  /** Navigates to a sibling page (route remounts detail). */
  navigate: (pageId: string) => void;
  /** Surface on which width is measured for ratio commit. */
  getSurfaceRef: () => HTMLElement | undefined;
}

export function useNoteCarousel(options: NoteCarouselOptions) {
  const [dragX, setDragX] = createSignal(0);
  const [isAnimating, setIsAnimating] = createSignal(false);

  const width = () => options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;

  /** Target for a direction; null if no sibling in that direction. */
  const targetFor = (direction: "next" | "prev"): string | null =>
    direction === "next" ? options.getNextId() : options.getPrevId();

  /** Animates exit of current panel and navigates; jumps on reduced-motion. */
  const commit = (direction: "next" | "prev", targetId: string) => {
    if (prefersReducedMotion()) {
      setDragX(0);
      options.navigate(targetId);
      return;
    }
    const outX = direction === "next" ? -width() : width();
    setIsAnimating(true);
    setDragX(outX);
    vibrate(HAPTIC_MS);
    setTimeout(() => {
      options.navigate(targetId);
      // Detail remounts keyed by pageId, but the hook lingers for an instant
      // (Solid reconciles the wrapper). Reset without transition so the
      // new content does not inherit the offset.
      setIsAnimating(false);
      setDragX(0);
    }, TRANSITION_MS);
  };

  const snapBack = () => {
    if (prefersReducedMotion()) {
      setDragX(0);
      return;
    }
    setIsAnimating(true);
    setDragX(0);
    setTimeout(() => {
      setIsAnimating(false);
    }, TRANSITION_MS);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    fling: { ...FLING },
    canStart: ({ target }) => {
      // Do not steal editor text selection or start on controls.
      if (isEditableTarget(target)) {
        return false;
      }
      return !closestSelector(target, 'button, a, input, [data-no-swipe="true"]');
    },
    onStart: () => {
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setIsAnimating(false);
      // Resistance when there is no sibling in drag direction ("end of list"
      // feel): dx<0 goes to next, dx>0 to previous.
      const wantsNext = sample.dx < 0;
      const hasTarget = wantsNext ? options.getNextId() !== null : options.getPrevId() !== null;
      setDragX(hasTarget ? sample.dx : applyRubberBand(sample.dx, 0, RUBBER_BAND_FACTOR));
    },
    onEnd: (sample) => {
      const direction: "next" | "prev" = sample.dx < 0 ? "next" : "prev";
      const targetId = targetFor(direction);
      const commitPx = Math.min(COMMIT_THRESHOLD_PX, width() * COMMIT_RATIO);
      const committed = targetId !== null && (sample.isFling || Math.abs(sample.dx) >= commitPx);
      if (committed && targetId) {
        commit(direction, targetId);
      } else {
        snapBack();
      }
    },
    onCancel: snapBack,
  });

  const base = pointerHandlers(recognizer);

  return {
    dragX,
    isAnimating,
    transitionMs: TRANSITION_MS,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
  };
}
